import type { LearningLibraryEntry, LearningTrackLibrary } from '../../lib/data'
import { hueFor } from './derive'
import { ExternalLink } from './icons'

// ============================================================================
// RESOURCES — the section the owner asked for by name (2026-08-28): "a separate section I can
// click into that shows all my learning resources."
//
// So: every link his plans point at, per track, and every row opens in a new tab. Not a reading
// list the app curated — a printout of what the imported plans actually name. The Worker derives
// it (`GET /api/learning/resources`), deduplicated by url, which is why two chapters of one book
// are one row here with a count beside them rather than two rows that look like two books.
//
// TWO BLOCKS PER TRACK, and they are genuinely different things:
//   · THE SHELF   — the plan's standing `resources`: the textbook, the exercise site. Opened every
//                   session, with or without an item asking.
//   · PER ITEM    — the links the items name. Each carries how many items use it, how many of
//                   those are done, and the next one that is not.
//
// A track whose plan named nothing says so in a sentence. An empty section that looked broken
// would send him to check whether the import failed, which it did not.
// ============================================================================

export function ResourceLibrary({ tracks }: { tracks: LearningTrackLibrary[] }) {
  if (tracks.length === 0) {
    return (
      <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
        <p className="max-w-[620px] text-body text-ink-3 text-pretty">
          No plan has been imported yet, so there are no resources to open. Plans are imported
          rather than written here — bring one in and its links appear on this shelf.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-[14px]">
      {tracks.map((track, index) => {
        const hue = hueFor(index)
        const shelf = track.entries.filter((e) => e.origin === 'track')
        const perItem = track.entries.filter((e) => e.origin === 'item')
        return (
          <section
            key={track.trackId}
            className="flex flex-col gap-[11px] rounded-context border border-line bg-surface px-5 py-[18px] shadow-card"
          >
            <header className="flex items-baseline gap-[9px]">
              <h2 className={['text-[15px] font-semibold leading-none tracking-[-0.01em]', hue.text].join(' ')}>
                {track.trackName}
              </h2>
              <span className="text-micro font-normal text-muted">
                {track.entries.length} {track.entries.length === 1 ? 'resource' : 'resources'}
              </span>
            </header>

            {track.entries.length === 0 && (
              <p className="text-body text-ink-3 text-pretty">
                This plan names no links at all — not on the track and not on any item. Re-import
                it with resources and they show up here.
              </p>
            )}

            {shelf.length > 0 && (
              <Block heading="The shelf" note="Opened every session.">
                {shelf.map((entry) => (
                  <ResourceRow key={entry.url} entry={entry} />
                ))}
              </Block>
            )}

            {perItem.length > 0 && (
              <Block heading="Per item" note="What the plan's own items point at.">
                {perItem.map((entry) => (
                  <ResourceRow key={entry.url} entry={entry} />
                ))}
              </Block>
            )}
          </section>
        )
      })}
    </div>
  )
}

function Block({
  heading,
  note,
  children,
}: {
  heading: string
  note: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-[7px]">
      <div className="flex items-baseline gap-[9px]">
        <h3 className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          {heading}
        </h3>
        <span className="text-micro font-normal text-microcopy">{note}</span>
      </div>
      <ul className="flex flex-col gap-[5px]">{children}</ul>
    </div>
  )
}

/**
 * One link. The whole row is the target — a library page where only the words are clickable is a
 * library page that is annoying to use on a laptop trackpad.
 */
function ResourceRow({ entry }: { entry: LearningLibraryEntry }) {
  return (
    <li>
      <a
        href={entry.url}
        target="_blank"
        rel="noreferrer noopener"
        title={`Open ${entry.label} in a new tab`}
        className="mo-ink mo-press flex items-center gap-[9px] rounded-card border border-line bg-surface px-[11px] py-[8px]"
      >
        <span className="flex-none rounded-pill bg-fill-2 px-[9px] py-[3px] text-pill font-medium text-ink-3">
          {entry.kind}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-label font-medium text-ink-2">{entry.label}</span>
          {/* The line under the label is the only claim this row makes, and it is counted from
              the plan: how many items use the link and which one is next. A standing resource
              nothing references says that instead of showing "0 items", which reads like a fault. */}
          <span className="mt-[3px] block truncate text-micro font-normal text-microcopy">
            {entry.itemCount === 0
              ? 'On the shelf — no item points at it'
              : entry.nextItemTitle
                ? `${entry.itemsDone} of ${entry.itemCount} done · next: ${entry.nextItemTitle}`
                : `${entry.itemCount} ${entry.itemCount === 1 ? 'item' : 'items'} · all done`}
          </span>
        </span>
        <ExternalLink size={12} className="flex-none text-muted" />
      </a>
    </li>
  )
}

export function ResourceLibrarySkeleton() {
  return (
    <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <span className="sr-only">Loading…</span>
      <div className="mb-[11px] h-[14px] w-[120px] rounded-full bg-fill" aria-hidden />
      <div className="mb-[5px] h-[34px] rounded-card bg-surface-2" aria-hidden />
      <div className="h-[34px] rounded-card bg-surface-2" aria-hidden />
    </div>
  )
}
